/**
 * Canadian sales tax for success-fee invoices.
 * Subscription Tax% on Managed Clients may be GST-only (e.g. BC 5%);
 * invoice schedules below use full fee tax where applicable.
 */

export const DEFAULT_TAX_PERCENT = 13;

/** Subscription / override combined rates (Managed Clients). */
export const PROVINCE_TAX_PERCENT = {
  ON: 13,
  BC: 5,
  AB: 5,
  SK: 11,
  MB: 12,
  QC: 14.975,
  NS: 15,
  NB: 15,
  NL: 15,
  PE: 15,
  PEI: 15,
  NT: 5,
  NU: 5,
  YT: 5,
};

/**
 * Full success-fee invoice tax components by province.
 * PST/QST included where typically charged on professional services.
 */
export const PROVINCE_INVOICE_TAX = {
  ON: { code: "ON", type: "HST", components: [{ label: "HST", rate: 13 }] },
  NB: { code: "NB", type: "HST", components: [{ label: "HST", rate: 15 }] },
  NL: { code: "NL", type: "HST", components: [{ label: "HST", rate: 15 }] },
  NS: { code: "NS", type: "HST", components: [{ label: "HST", rate: 15 }] },
  PE: { code: "PE", type: "HST", components: [{ label: "HST", rate: 15 }] },
  PEI: { code: "PE", type: "HST", components: [{ label: "HST", rate: 15 }] },
  BC: {
    code: "BC",
    type: "GST+PST",
    components: [
      { label: "GST", rate: 5 },
      { label: "PST", rate: 7 },
    ],
  },
  SK: {
    code: "SK",
    type: "GST+PST",
    components: [
      { label: "GST", rate: 5 },
      { label: "PST", rate: 6 },
    ],
  },
  MB: {
    code: "MB",
    type: "GST+PST",
    components: [
      { label: "GST", rate: 5 },
      { label: "PST", rate: 7 },
    ],
  },
  QC: {
    code: "QC",
    type: "GST+QST",
    components: [
      { label: "GST", rate: 5 },
      { label: "QST", rate: 9.975 },
    ],
  },
  AB: { code: "AB", type: "GST", components: [{ label: "GST", rate: 5 }] },
  NT: { code: "NT", type: "GST", components: [{ label: "GST", rate: 5 }] },
  NU: { code: "NU", type: "GST", components: [{ label: "GST", rate: 5 }] },
  YT: { code: "YT", type: "GST", components: [{ label: "GST", rate: 5 }] },
};

const PROVINCE_NAMES = {
  ONTARIO: "ON",
  "BRITISH COLUMBIA": "BC",
  ALBERTA: "AB",
  SASKATCHEWAN: "SK",
  MANITOBA: "MB",
  QUEBEC: "QC",
  "NOVA SCOTIA": "NS",
  "NEW BRUNSWICK": "NB",
  "NEWFOUNDLAND AND LABRADOR": "NL",
  "PRINCE EDWARD ISLAND": "PE",
};

export const normalizeProvince = (value) => {
  if (!value) return null;
  const raw = String(value).trim().toUpperCase();
  if (raw.length === 2) return raw === "PEI" ? "PE" : raw;
  return PROVINCE_NAMES[raw] || null;
};

export const parseTaxPercent = (value, fallback = DEFAULT_TAX_PERCENT) => {
  if (value === null || value === undefined || value === "") return fallback;
  const num = Number(String(value).replace("%", "").trim());
  if (Number.isNaN(num) || num < 0) return fallback;
  return num <= 1 ? num * 100 : num;
};

export const getTaxPercent = (fields = {}, tableDefaultPercent = DEFAULT_TAX_PERCENT) => {
  const fromField =
    fields.TaxPercent ?? fields.SubscriptionTaxPercent ?? fields.taxPercent;
  if (fromField !== null && fromField !== undefined && fromField !== "") {
    return parseTaxPercent(fromField, tableDefaultPercent);
  }
  const province = normalizeProvince(
    fields.ProvinceOrState ?? fields.Province ?? fields.State
  );
  if (province && PROVINCE_TAX_PERCENT[province] != null) {
    return PROVINCE_TAX_PERCENT[province];
  }
  return tableDefaultPercent;
};

export const formatTaxPercent = (percent) => {
  const rounded = Math.round(Number(percent) * 1000) / 1000;
  return Number.isInteger(rounded) ? `${rounded}%` : `${rounded}%`;
};

/**
 * Resolve labeled tax lines for an invoice subtotal.
 * If company TaxPercent is set, use a single combined override line labeled by province type.
 */
export const resolveInvoiceTax = (subtotal, companyFields = {}) => {
  const amount = Math.max(0, Number(subtotal) || 0);
  const province = normalizeProvince(
    companyFields.ProvinceOrState ??
      companyFields.Province ??
      companyFields.State
  );
  const schedule = province ? PROVINCE_INVOICE_TAX[province] : null;
  const overrideRaw =
    companyFields.TaxPercent ??
    companyFields.SubscriptionTaxPercent ??
    companyFields.taxPercent;
  const hasOverride =
    overrideRaw !== null && overrideRaw !== undefined && overrideRaw !== "";

  if (hasOverride) {
    const rate = parseTaxPercent(overrideRaw, schedule
      ? schedule.components.reduce((s, c) => s + c.rate, 0)
      : DEFAULT_TAX_PERCENT);
    const label =
      schedule?.type === "HST"
        ? "HST"
        : schedule?.type === "GST"
          ? "GST"
          : "Tax";
    const taxAmount = Math.round(amount * (rate / 100) * 100) / 100;
    return {
      province,
      type: schedule?.type || "Tax",
      lines: [{ label, rate, amount: taxAmount }],
      taxTotal: taxAmount,
      totalWithTax: amount + taxAmount,
      usedOverride: true,
    };
  }

  if (schedule) {
    const lines = schedule.components.map((c) => ({
      label: c.label,
      rate: c.rate,
      amount: Math.round(amount * (c.rate / 100) * 100) / 100,
    }));
    const taxTotal = lines.reduce((s, l) => s + l.amount, 0);
    return {
      province: schedule.code,
      type: schedule.type,
      lines,
      taxTotal,
      totalWithTax: amount + taxTotal,
      usedOverride: false,
    };
  }

  const rate = DEFAULT_TAX_PERCENT;
  const taxAmount = Math.round(amount * (rate / 100) * 100) / 100;
  return {
    province: null,
    type: "HST",
    lines: [{ label: "HST", rate, amount: taxAmount }],
    taxTotal: taxAmount,
    totalWithTax: amount + taxAmount,
    usedOverride: false,
  };
};
